import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, Plus, Edit, Trash2, ArrowLeft, UserPlus, UserMinus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CrewsSettings = () => {
  const navigate = useNavigate();
  const [crews, setCrews] = useState([]);
  const [users, setUsers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    leader_id: '',
    market_id: '',
    employment_type: '',
    pay_rate: '',
    crew_labor_percent: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes, marketsRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/users`),
        axios.get(`${API}/markets`)
      ]);
      
      // Filter teams that are crews (department = "Crews")
      const crewTeams = (teamsRes.data || []).filter(team => 
        team.department?.toLowerCase().includes('crew')
      );
      setCrews(crewTeams);
      setUsers(usersRes.data || []);
      setMarkets(marketsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load crews');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Crew name is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/teams`, {
        name: formData.name,
        description: formData.description,
        department: 'Crews',
        manager_id: formData.leader_id || null,
        market_id: formData.market_id || null,
        employment_type: formData.employment_type || null,
        pay_rate: formData.pay_rate ? parseFloat(formData.pay_rate) : null,
        crew_labor_percent: formData.crew_labor_percent ? parseFloat(formData.crew_labor_percent) : 0,
      });
      
      toast.success('Crew created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create crew:', error);
      toast.error('Failed to create crew');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Crew name is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.patch(`${API}/teams/${selectedCrew.id}`, {
        name: formData.name,
        description: formData.description,
        manager_id: formData.leader_id || null,
        market_id: formData.market_id || null,
        employment_type: formData.employment_type || null,
        pay_rate: formData.pay_rate ? parseFloat(formData.pay_rate) : null,
        crew_labor_percent: formData.crew_labor_percent ? parseFloat(formData.crew_labor_percent) : 0,
      });
      
      toast.success('Crew updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update crew:', error);
      toast.error('Failed to update crew');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCrew) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/teams/${selectedCrew.id}`);
      
      toast.success('Crew deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedCrew(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete crew:', error);
      toast.error('Failed to delete crew');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (crew) => {
    setSelectedCrew(crew);
    setFormData({
      name: crew.name || '',
      description: crew.description || '',
      leader_id: crew.manager_id || '',
      market_id: crew.market_id || '',
      employment_type: crew.employment_type || '',
      pay_rate: crew.pay_rate ? String(crew.pay_rate) : '',
      crew_labor_percent: crew.crew_labor_percent ? String(crew.crew_labor_percent) : '',
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (crew) => {
    setSelectedCrew(crew);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      leader_id: '',
      market_id: '',
      employment_type: '',
      pay_rate: '',
      crew_labor_percent: '',
    });
    setSelectedCrew(null);
  };

  const getMarketName = (marketId) => {
    const market = markets.find(m => m.id === marketId);
    return market ? market.name : null;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'Unassigned';
  };

  const getCrewMemberCount = (crew) => {
    // Count users assigned to this crew/team
    return crew.member_ids?.length || 0;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Crew Management</h1>
              <p className="text-muted-foreground">
                Create and manage work crews for job assignments
              </p>
            </div>
          </div>
          <Button onClick={() => {
            resetForm();
            setCreateDialogOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Crew
          </Button>
        </div>

        {/* Crews Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading crews...</div>
          </div>
        ) : crews.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Crews Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first crew to start assigning them to jobs
              </p>
              <Button onClick={() => {
                resetForm();
                setCreateDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Crew
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {crews.map(crew => (
              <Card key={crew.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/10">
                        <Users className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{crew.name}</CardTitle>
                        {crew.description && (
                          <CardDescription className="mt-1 line-clamp-2">
                            {crew.description}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Crew Leader */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Crew Leader:</span>
                      <span className="font-medium">
                        {crew.manager_id ? getUserName(crew.manager_id) : 'Not assigned'}
                      </span>
                    </div>
                    
                    {/* Market */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Market:</span>
                      <span className="font-medium">
                        {crew.market_id ? getMarketName(crew.market_id) : 'Not assigned'}
                      </span>
                    </div>

                    {/* Employment Type */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Type:</span>
                      {crew.employment_type ? (
                        <Badge variant={crew.employment_type === 'W2' ? 'default' : 'outline'}>
                          {crew.employment_type}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </div>

                    {/* Pay Rate */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Pay Rate:</span>
                      <span className="font-medium">
                        {crew.pay_rate ? `$${Number(crew.pay_rate).toFixed(2)}/hr` : 'Not set'}
                      </span>
                    </div>

                    {/* Crew Labor % */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Labor %:</span>
                      <span className="font-medium">
                        {crew.crew_labor_percent ? `${crew.crew_labor_percent}%` : 'Not set'}
                      </span>
                    </div>

                    {/* Member Count */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Members:</span>
                      <Badge variant="secondary">
                        {getCrewMemberCount(crew)} members
                      </Badge>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditDialog(crew)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteDialog(crew)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Crew Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Crew</DialogTitle>
              <DialogDescription>
                Add a new work crew that can be assigned to jobs
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Crew Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Crew Alpha, Team 1"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the crew..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="leader">Crew Leader</Label>
                <Select
                  value={formData.leader_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, leader_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select crew leader..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Leader Assigned --</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name || ''} {user.last_name || ''} {user.email ? `(${user.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="market">Market</Label>
                <Select
                  value={formData.market_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, market_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select market..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Market Assigned --</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>
                        {market.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employment_type">Employment Type</Label>
                  <Select
                    value={formData.employment_type || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, employment_type: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Not Set --</SelectItem>
                      <SelectItem value="W2">W2</SelectItem>
                      <SelectItem value="1099">1099</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pay_rate">Pay Rate ($/hr)</Label>
                  <Input
                    id="pay_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.pay_rate}
                    onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crew_labor_percent">Crew Labor % (of Gross Contract)</Label>
                  <Input
                    id="crew_labor_percent"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="e.g., 35"
                    value={formData.crew_labor_percent}
                    onChange={(e) => setFormData({ ...formData, crew_labor_percent: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    This % will auto-populate on estimates when this crew is assigned
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating...' : 'Create Crew'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Crew Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Crew</DialogTitle>
              <DialogDescription>
                Update crew information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Crew Name *</Label>
                <Input
                  id="edit-name"
                  placeholder="e.g., Crew Alpha, Team 1"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  placeholder="Brief description of the crew..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-leader">Crew Leader</Label>
                <Select
                  value={formData.leader_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, leader_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select crew leader..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Leader Assigned --</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name || ''} {user.last_name || ''} {user.email ? `(${user.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-market">Market</Label>
                <Select
                  value={formData.market_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, market_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select market..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Market Assigned --</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>
                        {market.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-employment_type">Employment Type</Label>
                  <Select
                    value={formData.employment_type || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, employment_type: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Not Set --</SelectItem>
                      <SelectItem value="W2">W2</SelectItem>
                      <SelectItem value="1099">1099</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-pay_rate">Pay Rate ($/hr)</Label>
                  <Input
                    id="edit-pay_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.pay_rate}
                    onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-crew_labor_percent">Crew Labor % (of Gross Contract)</Label>
                <Input
                  id="edit-crew_labor_percent"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="e.g., 35"
                  value={formData.crew_labor_percent}
                  onChange={(e) => setFormData({ ...formData, crew_labor_percent: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  This % will auto-populate on estimates when this crew is assigned
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Crew?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedCrew?.name}"? This action cannot be undone.
                Jobs currently assigned to this crew will need to be reassigned.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default CrewsSettings;
